import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './Layout';
import { HomePage } from './HomePage';
import OnboardingPage from './OnboardingPage';
import { MeditationCatalogPage } from './MeditationCatalogPage';
import { MeditationDetailPage } from './MeditationDetailPage';
import { TasksPage } from './TasksPage';
import { JournalListPage } from './JournalListPage';
import { AddJournalEntryPage } from './AddJournalEntryPage';
import { BalancePage } from './BalancePage';
import { ProgressPage } from './ProgressPage';
import { ProfilePage } from './ProfilePage';
import { PreferencesPage } from './PreferencesPage';
import { NotificationsPage } from './NotificationsPage';
import { ThemePage } from './ThemePage';
import { LanguagePage } from './LanguagePage';
import { AboutPage } from './AboutPage';
import { SupportPage } from './SupportPage';
import { SupportTicketsPage } from './SupportTicketsPage';
import { SupportTicketDetailPage } from './SupportTicketDetailPage';
import { FriendsPage } from './FriendsPage';
import { useTelegram } from '../hooks/useTelegram';
import { useTranslation } from '../i18n';

interface AppRouterProps {
  showOnboarding: boolean;
  onOnboardingComplete: () => void;
}

export const AppRouter: React.FC<AppRouterProps> = ({ showOnboarding, onOnboardingComplete }) => {
  const { isReady, isTelegramEnv } = useTelegram();
  const t = useTranslation();

  // In Telegram env, wait for WebApp to initialise; in browser mode skip
  if (isTelegramEnv && !isReady) {
    return (
      <div className="app-loading">
        <div className="loading-content">
          <div className="loading-icon">🧠</div>
          <div className="loading-spinner"></div>
          <p>{t.appLoading.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      {showOnboarding ? (
        <OnboardingPage onComplete={onOnboardingComplete} />
      ) : (
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/meditations" element={<MeditationCatalogPage />} />
            <Route path="/meditations/:id" element={<MeditationDetailPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/journal" element={<JournalListPage />} />
            <Route path="/journal/add" element={<AddJournalEntryPage />} />
            <Route path="/balance" element={<BalancePage />} />
            <Route path="/progress" element={<ProgressPage />} />
            <Route path="/friends" element={<FriendsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/profile/preferences" element={<PreferencesPage />} />
            <Route path="/profile/notifications" element={<NotificationsPage />} />
            <Route path="/profile/theme" element={<ThemePage />} />
            <Route path="/profile/language" element={<LanguagePage />} />
            <Route path="/profile/about" element={<AboutPage />} />
            <Route path="/profile/support" element={<SupportPage />} />
            <Route path="/profile/support/tickets" element={<SupportTicketsPage />} />
            <Route path="/profile/support/tickets/:id" element={<SupportTicketDetailPage />} />
            <Route path="/onboarding" element={<OnboardingPage onComplete={onOnboardingComplete} />} />
            {/* Catch all route - redirect to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      )}
    </BrowserRouter>
  );
};
